import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { CameraIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface MobileCameraHandlerProps {
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
  children: React.ReactNode;
}

const MobileCameraHandler: React.FC<MobileCameraHandlerProps> = ({
  onPermissionGranted,
  onPermissionDenied,
  children
}) => {
  const [permissionStatus, setPermissionStatus] = useState<'checking' | 'granted' | 'denied' | 'unavailable'>('checking');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkCameraPermission();
  }, []);

  const checkCameraPermission = async () => {
    try {
      // Check if we're on a mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (!isMobile) {
        setPermissionStatus('granted');
        onPermissionGranted();
        return;
      }

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setPermissionStatus('unavailable');
        setError('Camera access is not supported on this device');
        onPermissionDenied();
        return;
      }

      // Check if we're on HTTPS (required for camera access on mobile)
      if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
        setPermissionStatus('denied');
        setError('Camera access requires HTTPS. Please use HTTPS to access the camera.');
        onPermissionDenied();
        return;
      }

      // Try to get camera permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        
        // Stop the stream immediately as we just wanted to check permission
        stream.getTracks().forEach(track => track.stop());
        
        setPermissionStatus('granted');
        onPermissionGranted();
      } catch (err: any) {
        console.error('Camera permission error:', err);
        
        if (err.name === 'NotAllowedError') {
          setPermissionStatus('denied');
          setError('Camera permission denied. Please allow camera access and try again.');
        } else if (err.name === 'NotFoundError') {
          setPermissionStatus('unavailable');
          setError('No camera found on this device.');
        } else if (err.name === 'NotSupportedError') {
          setPermissionStatus('unavailable');
          setError('Camera access is not supported on this device.');
        } else {
          setPermissionStatus('denied');
          setError('Unable to access camera. Please check your browser settings.');
        }
        
        onPermissionDenied();
      }
    } catch (err) {
      console.error('Error checking camera permission:', err);
      setPermissionStatus('denied');
      setError('An error occurred while checking camera permissions.');
      onPermissionDenied();
    }
  };

  const requestPermission = async () => {
    setPermissionStatus('checking');
    setError(null);
    await checkCameraPermission();
  };

  if (permissionStatus === 'checking') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '300px',
        p: 3
      }}>
        <CameraIcon className="h-12 w-12 text-blue-500 mb-4" />
        <Typography variant="h6" gutterBottom>
          Checking Camera Access...
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please wait while we check camera permissions
        </Typography>
      </Box>
    );
  }

  if (permissionStatus === 'granted') {
    return <>{children}</>;
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '300px',
      p: 3
    }}>
      <ExclamationTriangleIcon className="h-12 w-12 text-orange-500 mb-4" />
      
      <Typography variant="h6" gutterBottom>
        Camera Access Required
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
          {error}
        </Alert>
      )}
      
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
        To scan QR codes, this app needs access to your camera. 
        Please grant camera permission and try again.
      </Typography>
      
      <Button
        variant="contained"
        onClick={requestPermission}
        startIcon={<CameraIcon className="h-5 w-5" />}
        sx={{ mb: 2 }}
      >
        Grant Camera Permission
      </Button>
      
      <Typography variant="caption" color="text.secondary" textAlign="center">
        If the issue persists, try refreshing the page or checking your browser settings.
      </Typography>
    </Box>
  );
};

export default MobileCameraHandler;
